import { Component, Input, Output, EventEmitter, OnDestroy, AfterViewChecked, ViewChild, ElementRef, Renderer2, HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IntervalObservable } from 'rxjs/observable/IntervalObservable';
import { Moment } from 'moment';
import * as numeral from 'numeral/numeral';
import 'rxjs/add/operator/takeWhile';
import { } from '@types/google.visualization';

import { CompareItem } from 'app/buffer-graph/models/compare-item';
import { ChartData } from 'app/buffer-graph/models/chart-data';

//declare var google: any;

@Component({
    selector: 'buffer-graph-chart',
    templateUrl: './buffer-graph-chart.component.html',
    styleUrls: ['./buffer-graph-chart.component.css']
})
export class BufferGraphChartComponent implements OnDestroy, AfterViewChecked {
    constructor(private readonly translateService: TranslateService) {
        this.isExtChartVisible = localStorage['BgShowExtChart'] !== '0';
        this.chartDrawed = new EventEmitter<void>();
        this.showAllChart = new EventEmitter<void>();
        this.showCompareChart = new EventEmitter<number>();
        this.closeCompareChart = new EventEmitter<void>();
        this.changeBuffer = new EventEmitter<void>();
        this.hScrollPositionCoeff = 1;
        this.vScrollPositionCoeff = 1;

        this.legendSeries = [
            { key: 'BgShowBalance', text: 'Balance', color: '#87CEFA' },
            { key: 'BgShowConsumed', text: 'Consumed', color: '#008080' },
            { key: 'BgShowSupplied', text: 'Supplied', color: '#DAA520' },
            { key: 'BgShowCheckedBalance', text: 'CheckedBalance', color: '#006400' },
            { key: 'BgShowPackage', text: 'Package', color: '#8B008B' },
            { key: 'BgShowShelfQty', text: 'ShelfQuantity', color: '#0000FF' },
            { key: 'BgShowReplTime', text: 'Rt', color: '#8FBC8F' },
            { key: 'BgShowMaxQnt', text: 'MaximalQuantityOnShelf', color: '#4169E1' },
            { key: 'BgShowMaxBuffer', text: 'MaximalBuffer', color: '#1E90FF' }
        ];
        this.bufferChartSeriesVisibility = {};
        this.legendSeries/*.concat([{ key: 'BgShowTargetLevel', text: null, color: null }])*/.forEach(node => this.bufferChartSeriesVisibility[node.key] = localStorage[node.key] !== '0');

        this.translates = {};
        this.translateService.get([
            'Sku', 'SkuName', 'WarehouseCode', 'Warehouse', 'ItemId', 'SafetyBuffer', 'Buffer', 'Consumed', 'Supplied', 'CheckedBalance', 'Balance',
            'TargetStockLevel', 'Package', 'Rt', 'ShelfQuantity', 'MaximalQuantityOnShelf', 'MaximalBuffer', 'Date', 'SafetyStock', 'Action_Sale', 'Season',
            'Vacation', 'Holiday', 'OrderDay', 'DeliveryDay', 'BufferGrowFixed', 'BufferDecreaseFixed'
        ]).subscribe(t => {
            this.translates['Sku'] = t.Sku;
            this.translates['SkuName'] = t.SkuName;
            this.translates['WarehouseCode'] = t.WarehouseCode;
            this.translates['Warehouse'] = t.Warehouse;
            this.translates['ItemId'] = t.ItemId;

            this.translates['Date'] = t.Date;
            this.translates['SafetyStock'] = t.SafetyStock;
            this.translates['Buffer'] = t.Buffer;
            this.translates['Consumed'] = t.Consumed;
            this.translates['Supplied'] = t.Supplied;
            this.translates['Balance'] = t.Balance;
            this.translates['CheckedBalance'] = t.CheckedBalance;
            this.translates['TargetStockLevel'] = t.TargetStockLevel;
            this.translates['Package'] = t.Package;
            this.translates['Rt'] = t.Rt;
            this.translates['ShelfQuantity'] = t.ShelfQuantity;
            this.translates['MaximalQuantityOnShelf'] = t.MaximalQuantityOnShelf;
            this.translates['MaximalBuffer'] = t.MaximalBuffer;

            this.translates['Action_Sale'] = t.Action_Sale;
            this.translates['Season'] = t.Season;
            this.translates['Vacation'] = t.Vacation;
            this.translates['Holiday'] = t.Holiday;
            this.translates['OrderDay'] = t.OrderDay;
            this.translates['DeliveryDay'] = t.DeliveryDay;
            this.translates['BufferGrowFixed'] = t.BufferGrowFixed;
            this.translates['BufferDecreaseFixed'] = t.BufferDecreaseFixed;

            this.translatesReceived = true;
        });
    }

    @ViewChild('bufferChart')
    private bufferChartRef: ElementRef;

    @ViewChild('extChart')
    private extChartRef: ElementRef;

    @Input()
    private isMainChart: boolean;

    @Input()
    private bufferChartElementId: string;

    @Input()
    private extChartElementId: string;


    private data: ChartData;
    @Input()
    private set chartData(data: ChartData) {
        this.data = data;

        if (!data)
            return;

        this.isVisible = false;

        if (!data.isMaxQuantityDisplayable)
            this.bufferChartSeriesVisibility['BgShowMaxQnt'] = false;
        if (!data.isMaxBufferDisplayable)
            this.bufferChartSeriesVisibility['BgShowMaxBuffer'] = false;

        this.vScrollPositionCoeff = 1;
        this.hScrollPositionCoeff = data.rows.length / (data.rows.length + this.extraScrollDays);

        this.tryDrawCharts();
    }

    @Input()
    private cwhItemId: number;

    @Input()
    private compareItems: CompareItem[];

    @Input()
    private set showTargetLevel(value: boolean) {
        if (this.bufferChartSeriesVisibility['BgShowTargetLevel'] === value)
            return;

        this.bufferChartSeriesVisibility['BgShowTargetLevel'] = value;
        if (this.isMainChart && this.bufferChart)
            this.redrawBufferChartOnSeriesChange();
    }

    @Output()
    private readonly chartDrawed: EventEmitter<void>;

    @Output()
    private readonly showAllChart: EventEmitter<void>;

    @Output()
    private readonly showCompareChart: EventEmitter<number>;

    @Output()
    private readonly closeCompareChart: EventEmitter<void>;

    @Output()
    private readonly changeBuffer: EventEmitter<void>;

    private readonly leftMargin = 50;

    private readonly topMargin = 40;

    private readonly rightMargin = 40;

    private readonly bottomMargin = 60;

    private readonly leftExtMargin = 40;

    private readonly rightExtMargin = 60;

    private readonly topExtMargin = 5;

    private readonly extraScrollDays = 1;

    private isExtChartVisible: boolean;

    private readonly translates: { [key: string]: string };

    private translatesReceived: boolean;

    private waitingForDraw: boolean;

    private isVisible: boolean;

    private destroyed: boolean;

    private bufferChart: google.visualization.ChartWrapper;

    private bufferChartOptions: any;

    //private bufferChartDataView: google.visualization.DataView;

    private extChart: google.visualization.ChartWrapper;

    private extChartOptions: any;

    private readonly legendSeries: { key: string, text: string, color: string }[];

    private readonly bufferChartSeriesVisibility: { [key: string]: boolean };

    private redrawChartsAfterViewChecked: boolean;

    //private redrawChartsAfterViewCheckedTimer: any;

    private vScrollHeight: number;

    private vScrollPosition: number;

    private vScrollPositionCoeff: number;

    private vScrollTimer: any;

    private skipVScroll: boolean;

    private hScrollWidth: number;

    private hScrollPosition: number;

    private hScrollPositionCoeff: number;

    private hScrollTimer: any;

    private skipHScroll: boolean;

    private wheelScroll: boolean;

    ngAfterViewChecked() {
        if (!this.redrawChartsAfterViewChecked || !this.isVisible)
            return;

        this.redrawChartsAfterViewChecked = false;
        this.resizeAndRedrawCharts();
    }

    ngOnDestroy() {
        this.destroyed = true;
    }

    resizeAndRedrawCharts = (): void => {
        setTimeout(() => {
            this.setBufferChartSize();
            this.bufferChart.draw();

            if (!this.isExtChartVisible)
                return;

            this.setExtChartSize();
            this.extChart.draw();
        },
            1);
    }

    private getCompareItemBalanceClass = (item: CompareItem): string => {
        switch (item.balanceStatus) {
            case 1:
                return 'compare-balance-zero';
            case 2:
                return 'compare-balance-safetystock';
            case 3:
                return 'compare-balance-red';
            case 4:
                return 'compare-balance-yellow';
            case 5:
                return 'compare-balance-green';
            case 6:
                return 'compare-balance-overstock';
            default:
                return '';
        }
    }

    private canDrawChart = (): boolean => {
        return this.translatesReceived &&
            this.bufferChartRef != null &&
            this.bufferChartRef.nativeElement.id === this.bufferChartElementId &&
            this.extChartRef != null &&
            this.extChartRef.nativeElement.id === this.extChartElementId;
    }

    private tryDrawCharts = (): void => {
        if (this.canDrawChart()) {
            this.drawCharts();
            return;
        }

        if (this.waitingForDraw)
            return;

        this.waitingForDraw = true;
        IntervalObservable.create(50).takeWhile(() => !this.destroyed && this.waitingForDraw).subscribe(() => {
            if (!this.canDrawChart())
                return;

            this.waitingForDraw = false;
            this.drawCharts();
        });
    }

    private drawCharts = (): void => {
        this.drawBufferChart();

        this.drawExtChart();
    }

    private drawBufferChart = (): void => {
        const data = this.data;
        const title = `${this.translates['Sku']} = ${data.itemInfo.sku}, ${this.translates['SkuName']} = ${data.itemInfo.name}, ${this
            .translates['WarehouseCode']} = ${data.itemInfo.warehouseCode}, ${this.translates['Warehouse']} = ${data.itemInfo
                .warehouseName}${data.itemInfo.displayItemId ? `, ${this.translates['ItemId']} = ${data.itemInfo.itemId}` : ''}`;
        this.bufferChartOptions = {
            title: title,
            isHtml: true,
            hAxis: { viewWindow: { min: data.rows.length - data.visibleDaysCount, max: data.rows.length }, textStyle: { fontSize: 11 } },
            vAxis: { viewWindow: { min: data.min, max: data.max + (data.min === data.max ? 1 : 0) }, textStyle: { fontSize: 12 }, gridlines: { color: '#000000' }, minorGridlines: { color: '#CCCCCC', count: 3 } },
            seriesType: 'line',
            series: this.getBufferChartSeries(),
            vAxes: { 1: { viewWindow: { min: 0/*data.rtMin - (data.rtMin == data.rtMax ? 1 : 0)*/, max: data.rtMax + (data.rtMin === data.rtMax ? 1 : 0) }, textStyle: { color: 'Red' }, gridlines: { color: 'Red' }, minorGridlines: { count: 0 } } },
            areaOpacity: 1,
            legend: { position: 'none'/*, textStyle: { fontSize: 11 }*/ },
            tooltip: { isHtml: true /*trigger: 'none'*/ }
        };
        this.bufferChart = new google.visualization.ChartWrapper({
            chartType: 'ComboChart',
            containerId: this.bufferChartElementId,
            options: this.bufferChartOptions,
            view: { columns: this.getBufferChartColumns() },
            dataTable: this.getBufferChartDataTable()
        });
        this.setBufferChartSize();

        google.visualization.events.addListener(this.bufferChart,
            'ready',
            () => {
                if (this.isVisible)
                    return;

                this.chartDrawed.emit();
                this.isVisible = true;
            });
        this.bufferChart.draw();
    }

    private getBufferChartSeries = (): { [index: number]: any } => {
        let index = 0;
        const series = {};
        series[index++] = { color: '#AAFFAA', type: 'area', visibleInLegend: false, tooltip: false, lineWidth: 0 };          //Buffer
        series[index++] = { color: '#40FF40', type: 'area', visibleInLegend: false, tooltip: false, lineWidth: 0 };          //BufferG
        series[index++] = { color: '#FFFF40', type: 'area', visibleInLegend: false, tooltip: false, lineWidth: 0 };          //BufferY
        series[index++] = { color: '#FF4040', type: 'area', visibleInLegend: false, tooltip: false, lineWidth: 0 };          //BufferR
        series[index++] = { color: '#DD0000', type: 'area', visibleInLegend: false, tooltip: false, lineWidth: 0 };          //ActionSafetyBuffer
        series[index++] = { color: '#800000', type: 'area', visibleInLegend: false, tooltip: true, lineWidth: 0 };           //SafetyBuffer
        series[index++] = { color: '#800000', visibleInLegend: false, tooltip: true, pointSize: 4, lineWidth: 0 };          //ActionSafetyBuffer
        series[index++] = { color: '#90EE90', visibleInLegend: false, tooltip: true, pointSize: 4, lineWidth: 0 };           //Buffer
        if (this.bufferChartSeriesVisibility['BgShowConsumed'])
            series[index++] = { color: '#008080', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //Consumed
        if (this.bufferChartSeriesVisibility['BgShowSupplied'])
            series[index++] = { color: '#DAA520', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //Supplied
        if (this.bufferChartSeriesVisibility['BgShowCheckedBalance'])
            series[index++] = { color: '#006400', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //CheckedBalance
        if (this.bufferChartSeriesVisibility['BgShowBalance']) {
            series[index++] = { color: '#000000', visibleInLegend: false, tooltip: false, lineWidth: 0, pointSize: 10 };     //NoData
            series[index++] = { color: '#87CEFA', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //Balance
        }
        if (this.bufferChartSeriesVisibility['BgShowTargetLevel'])
            series[index++] = { color: '#000000', visibleInLegend: false, tooltip: true };                                   //TargetStockLevel
        if (this.bufferChartSeriesVisibility['BgShowPackage'])
            series[index++] = { color: '#8B008B', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //Package
        if (this.bufferChartSeriesVisibility['BgShowReplTime'])
            series[index++] = { color: '#8FBC8F', visibleInLegend: false, tooltip: true, pointSize: 4, targetAxisIndex: 1 }; //RT
        if (this.bufferChartSeriesVisibility['BgShowShelfQty'])
            series[index++] = { color: '#0000FF', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //ShelfQty
        if (this.bufferChartSeriesVisibility['BgShowMaxQnt'])
            series[index++] = { color: '#4169E1', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //MaxQuantity
        if (this.bufferChartSeriesVisibility['BgShowMaxBuffer'])
            series[index++] = { color: '#1E90FF', visibleInLegend: false, tooltip: true, pointSize: 4 };                     //MaxBuffer

        return series;
    }

    private getBufferChartColumns = (): number[] => {
        const columns = [];
        columns.push(0);                                                //Date
        columns.push(6);                                                //Buffer
        columns.push(1);                                                //BufferG
        columns.push(2);                                                //BufferY
        columns.push(3);                                                //BufferR
        columns.push(25);                                               //ActionSafetyBuffer
        columns.push(4);                                                //SafetyBuffer
        columns.push(25);                                               //ActionSafetyBuffer
        columns.push(5);
        columns.push(6);                                                //Buffer
        columns.push(7);
        if (this.bufferChartSeriesVisibility['BgShowConsumed']) {
            columns.push(8);                                            //Consumed
            columns.push(9);
        }
        if (this.bufferChartSeriesVisibility['BgShowSupplied']) {
            columns.push(10);                                           //Supplied
            columns.push(11);
        }
        if (this.bufferChartSeriesVisibility['BgShowCheckedBalance']) {
            columns.push(12);                                           //CheckedBalance
            columns.push(13);
        }
        if (this.bufferChartSeriesVisibility['BgShowBalance']) {
            columns.push(14);                                           //NoData
            columns.push(15);                                           //Balance
            columns.push(16);
        }
        if (this.bufferChartSeriesVisibility['BgShowTargetLevel']) {
            columns.push(17);                                           //TargetStockLevel
            columns.push(18);
        }
        if (this.bufferChartSeriesVisibility['BgShowPackage']) {
            columns.push(19);                                           //Package
            columns.push(20);
        }
        if (this.bufferChartSeriesVisibility['BgShowReplTime']) {
            columns.push(21);                                           //RT
            columns.push(22);
        }
        if (this.bufferChartSeriesVisibility['BgShowShelfQty']) {
            columns.push(23);                                           //ShelfQty
            columns.push(24);
        }
        if (this.bufferChartSeriesVisibility['BgShowMaxQnt']) {
            columns.push(26);                                           //MaxQuantity
            columns.push(27);
        }
        if (this.bufferChartSeriesVisibility['BgShowMaxBuffer']) {
            columns.push(28);                                           //MaxBuffer
            columns.push(29);
        }

        return columns;
    }

    private getBufferChartDataTable = (): google.visualization.DataTable => {
        const getTooltip = (date: Moment, fieldKey: string, value: number): string => {
            return `<table class="text-nowrap"><tr><td class="pr-1">${this.translates['Date']}:</td><td>${date.format('L')}</td></tr><tr><td class="pr-1">${this.translates[fieldKey]}:</td><td>${numeral(value).format('0,0.00')}</td></tr></table>`;
        };
        const table = new google.visualization.DataTable();

        table.addColumn('string', 'Date');                                       //0
        table.addColumn('number', 'BufferG');                                    //1
        table.addColumn('number', 'BufferY');                                    //2
        table.addColumn('number', 'BufferR');                                    //3
        table.addColumn('number', 'SafetyBuffer');                               //4
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //5
        table.addColumn('number', 'Buffer');                                     //6
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //7
        table.addColumn('number', 'Consumed');                                   //8
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //9
        table.addColumn('number', 'Supplied');                                   //10
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //11
        table.addColumn('number', 'CheckedBalance');                             //12
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //13
        table.addColumn('number', 'NoData');                                     //14
        table.addColumn('number', 'Balance');                                    //15
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //16
        table.addColumn('number', 'TargetStockLevel');                           //17
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //18
        table.addColumn('number', 'Package');                                    //19
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //20
        table.addColumn('number', 'RT');                                         //21
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //22
        table.addColumn('number', 'ShelfQty');                                   //23
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //24
        table.addColumn('number', 'ActionSafetyBuffer');                         //25
        table.addColumn('number', 'MaxQuantity');                                //26
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //27
        table.addColumn('number', 'MaxBuffer');                                  //28
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } }); //29

        table.addRows(this.data.rows.map(r => [
            r.date.format('L'),
            r.bufferG,
            r.bufferY,
            r.bufferR,
            r.safetyBuffer,
            getTooltip(r.date, 'SafetyStock', r.actionSafetyBuffer),
            r.actionBuffer,
            getTooltip(r.date, 'Buffer', r.actionBuffer - r.actionSafetyBuffer),
            r.valueOut,
            getTooltip(r.date, 'Consumed', r.valueOut),
            r.valueIn,
            getTooltip(r.date, 'Supplied', r.valueIn),
            r.checkedBalance,
            getTooltip(r.date, 'CheckedBalance', r.checkedBalance),
            r.noData,
            r.balance,
            getTooltip(r.date, 'Balance', r.balance),
            r.targetStockLevel,
            getTooltip(r.date, 'TargetStockLevel', r.targetStockLevel),
            r.packageValue,
            getTooltip(r.date, 'Package', r.packageValue),
            r.replenishmentTime,
            getTooltip(r.date, 'Rt', r.replenishmentTime),
            r.shelfQuantity,
            getTooltip(r.date, 'ShelfQuantity', r.shelfQuantity),
            r.actionSafetyBuffer,
            r.maxQuantity,
            getTooltip(r.date, 'MaximalQuantityOnShelf', r.maxQuantity),
            r.maxBuffer,
            getTooltip(r.date, 'MaximalBuffer', r.maxBuffer)
        ]));

        return table;
    }

    private setBufferChartSize = (): void => {
        const width = this.bufferChartRef.nativeElement.offsetWidth;
        const height = this.bufferChartRef.nativeElement.offsetHeight;
        this.bufferChartOptions.width = width;
        this.bufferChartOptions.height = height;
        this.bufferChartOptions.chartArea = { left: this.leftMargin, top: this.topMargin, width: width - this.leftMargin - this.rightMargin, height: height - this.topMargin - this.bottomMargin };

        this.setVerticalScrollProps();
        this.setHorizontalScrollProps();
    }

    private setVerticalScrollProps = (): void => {
        //Calculate the height of entire chart
        const chartHeight = this.bufferChartOptions.chartArea.height /
            (this.bufferChartOptions.vAxis.viewWindow.max - this.bufferChartOptions.vAxis.viewWindow.min) *
            this.data.max;
        //Calculate the ratio beetwen scrollbar and chart area
        const ratio = this.bufferChartRef.nativeElement.offsetHeight /
            this.bufferChartOptions.chartArea.height;
        this.vScrollHeight = chartHeight * ratio;
        setTimeout(() => this.vScrollPosition = this.vScrollHeight * this.vScrollPositionCoeff - this.bufferChartRef.nativeElement.offsetHeight, 1);
        this.skipVScroll = true;
    }

    private setHorizontalScrollProps = (): void => {
        //Calculate the width of entire chart
        const chartWidth = this.bufferChartOptions.chartArea.width /
            this.data.visibleDaysCount *
            (this.data.rows.length + this.extraScrollDays*2);
        //Calculate the ratio beetwen scrollbar and chart area
        const ratio = this.bufferChartRef.nativeElement.offsetWidth /
            this.bufferChartOptions.chartArea.width;
        this.hScrollWidth = chartWidth * ratio;
        setTimeout(() => this.hScrollPosition = this.hScrollWidth * this.hScrollPositionCoeff - this.bufferChartRef.nativeElement.offsetWidth, 1);
        this.skipHScroll = true;
    }

    private redrawBufferChartOnSeriesChange = (): void => {
        this.bufferChartOptions.series = this.getBufferChartSeries();
        this.bufferChart.setView({ columns: this.getBufferChartColumns() });
        this.bufferChart.draw();
    }

    private drawExtChart = (): void => {
        const data = this.data;
        this.extChartOptions = {
            isHtml: true,
            hAxis: { viewWindow: { min: data.rows.length - data.visibleDaysCount, max: data.rows.length }, textStyle: { fontSize: 11 } },
            vAxis: { textStyle: { fontSize: 12 }, gridlines: { count: 0 }, minorGridlines: { count: 0 } },
            seriesType: 'line',
            series: this.getExtChartSeries(),
            legend: { position: 'bottom', textStyle: { fontSize: 11 } },
            tooltip: { isHtml: true/*trigger: 'none'*/ }
        };
        this.extChart = new google.visualization.ChartWrapper({
            chartType: 'LineChart',
            containerId: this.extChartElementId,
            options: this.extChartOptions,
            view: { columns: this.getExtChartColumns() },
            dataTable: this.getExtChartDataTable()
        });

        if (!this.isExtChartVisible)
            return;

        this.setExtChartSize();

        this.extChart.draw();
    }

    private getExtChartSeries = (): { [index: number]: any } => {
        var series = {};
        var index = 0;
        if (this.data.showAction)
            series[index++] = { color: '#FF0000', lineWidth: 0, pointSize: 4 };
        if (this.data.showSeason)
            series[index++] = { color: '#00FF00', lineWidth: 0, pointSize: 4 };
        if (this.data.showVacation)
            series[index++] = { color: '#0000FF', lineWidth: 0, pointSize: 4 };
        if (this.data.showHoliday)
            series[index++] = { color: '#DA70D6', lineWidth: 0, pointSize: 4 };
        if (this.data.showOrderDay)
            series[index++] = { color: '#00FFFF', lineWidth: 0, pointSize: 4 };
        if (this.data.showDeliveryDay)
            series[index++] = { color: '#008000', lineWidth: 0, pointSize: 4 };
        if (this.data.showBufferGrowFixed)
            series[index++] = { color: '#B22222', lineWidth: 0, pointSize: 4 };
        if (this.data.showBufferDecreaseFixed)
            series[index++] = { color: '#000080', lineWidth: 0, pointSize: 4 };

        return series;
    }

    private getExtChartColumns = (): number[] => {
        var columns = [];
        columns.push(0);
        if (this.data.showAction) {
            columns.push(1);
            columns.push(2);
        }
        if (this.data.showSeason) {
            columns.push(3);
            columns.push(4);
        }
        if (this.data.showVacation) {
            columns.push(5);
            columns.push(6);
        }
        if (this.data.showHoliday) {
            columns.push(7);
            columns.push(8);
        }
        if (this.data.showOrderDay) {
            columns.push(9);
            columns.push(10);
        }
        if (this.data.showDeliveryDay) {
            columns.push(11);
            columns.push(12);
        }
        if (this.data.showBufferGrowFixed) {
            columns.push(13);
            columns.push(14);
        }
        if (this.data.showBufferDecreaseFixed) {
            columns.push(15);
            columns.push(16);
        }

        return columns;
    }

    private getExtChartDataTable = (): google.visualization.DataTable => {
        const getTooltip = (date: Moment, fieldKey: string): string => {
            return `<span class="text-nowrap">${this.translates[fieldKey]}: ${date.format('L')}</span>`;
        };
        const table = new google.visualization.DataTable();
        table.addColumn('string', 'Date');
        table.addColumn('number', this.translates['Action_Sale']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['Season']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['Vacation']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['Holiday']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['OrderDay']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['DeliveryDay']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['BufferGrowFixed']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });
        table.addColumn('number', this.translates['BufferDecreaseFixed']);
        table.addColumn({ type: 'string', role: 'tooltip', p: { html: true } });

        let value = 1;
        const values = {};
        values['Action'] = this.data.showAction ? value++ : 0;
        values['Season'] = this.data.showSeason ? value++ : 0;
        values['Vacation'] = this.data.showVacation ? value++ : 0;
        values['Holiday'] = this.data.showHoliday ? value++ : 0;
        values['OrderDay'] = this.data.showOrderDay ? value++ : 0;
        values['DeliveryDay'] = this.data.showDeliveryDay ? value++ : 0;
        values['BufferGrowFixed'] = this.data.showBufferGrowFixed ? value++ : 0;
        values['BufferDecreaseFixed'] = this.data.showBufferDecreaseFixed ? value++ : 0;

        table.addRows(this.data.rows.map(r => {
            const array = [
                r.date.format('L'),
                r.action ? values['Action'] : null,
                r.action ? getTooltip(r.date, 'Action_Sale') : null,
                r.season ? values['Season'] : null,
                r.season ? getTooltip(r.date, 'Season') : null,
                r.vacation ? values['Vacation'] : null,
                r.vacation ? getTooltip(r.date, 'Vacation') : null,
                r.holiday ? values['Holiday'] : null,
                r.holiday ? getTooltip(r.date, 'Holiday') : null,
                r.orderDay ? values['OrderDay'] : null,
                r.orderDay ? getTooltip(r.date, 'OrderDay') : null,
                r.deliveryDay ? values['DeliveryDay'] : null,
                r.deliveryDay ? getTooltip(r.date, 'DeliveryDay') : null,
                r.bufferGrowFixed ? values['BufferGrowFixed'] : null,
                r.bufferGrowFixed ? getTooltip(r.date, 'BufferGrowFixed') : null,
                r.bufferDecreaseFixed ? values['BufferDecreaseFixed'] : null,
                r.bufferDecreaseFixed ? getTooltip(r.date, 'BufferDecreaseFixed') : null,
            ];
            return array;
        }));

        return table;
    }

    private setExtChartSize = (): void => {
        const width = this.extChartRef.nativeElement.offsetWidth;
        const height = this.extChartRef.nativeElement.offsetHeight;
        this.extChartOptions.width = width;
        this.extChartOptions.height = height;
        this.extChartOptions.chartArea = { left: this.leftExtMargin, top: this.topExtMargin, width: width - this.leftMargin - this.rightExtMargin, height: height - this.topExtMargin - this.bottomMargin };
    }

    private onToggleExtChartClick = (): void => {
        this.isExtChartVisible = !this.isExtChartVisible;

        if (this.isMainChart)
            localStorage['BgShowExtChart'] = this.isExtChartVisible ? '1' : '0';

        this.redrawChartsAfterViewChecked = true;
    }

    private onLegendToggle = (key: string): void => {
        this.bufferChartSeriesVisibility[key] = !this.bufferChartSeriesVisibility[key];
        localStorage[key] = this.bufferChartSeriesVisibility[key] ? '1' : '0';
        this.redrawBufferChartOnSeriesChange();
    }

    @HostListener('window:resize')
    private onResize = (): void => {
        this.redrawChartsAfterViewChecked = true;
    }

    private onShowAllChartClick = (): void => {
        this.showAllChart.emit();
    }

    private onCompareClick = (itemId: number): void => {
        this.showCompareChart.emit(itemId);
    }

    private onCloseCompareChartClick = (): void => {
        this.closeCompareChart.emit();
    }

    private onZoomClick = (direction: number): void => {
        const delta = this.data.max * (direction > 0 ? 10 : direction < 0 ? -10 : 0) / 100;

        let min: number;
        let max: number;
        if (delta === 0) {
            min = this.data.min;
            max = this.data.max;
        } else {
            min = this.bufferChartOptions.vAxis.viewWindow.min;
            max = this.bufferChartOptions.vAxis.viewWindow.max + delta;
        }

        if (max <= this.bufferChartOptions.vAxis.viewWindow.min - delta)
            return;

        this.bufferChartOptions.vAxis.viewWindow.min = min;
        this.bufferChartOptions.vAxis.viewWindow.max = max;

        this.setVerticalScrollProps();

        this.bufferChart.draw();
    }

    private onScrollVertical = (event: UIEvent): void => {
        if (this.skipVScroll) {
            this.skipVScroll = false;
            return;
        }

        if (this.vScrollTimer)
            clearTimeout(this.vScrollTimer);

        const value = (event.target as HTMLElement).scrollTop;
        if (this.vScrollPosition === value)
            return;

        this.vScrollPosition = value;
        this.vScrollTimer = setTimeout(() => {
            this.vScrollTimer = null;
            this.vScrollPositionCoeff = (this.vScrollPosition + this.bufferChartRef.nativeElement.offsetHeight) / this.vScrollHeight;

            const max = (this.bufferChartOptions.vAxis.viewWindow.max - this.bufferChartOptions.vAxis.viewWindow.min) / this.vScrollPositionCoeff;
            const min = max - (this.bufferChartOptions.vAxis.viewWindow.max - this.bufferChartOptions.vAxis.viewWindow.min);
            this.bufferChartOptions.vAxis.viewWindow.min = min;
            this.bufferChartOptions.vAxis.viewWindow.max = max;

            this.bufferChart.draw();
        },
            100);
    }

    private onScrollHorizontal = (event: UIEvent): void => {
        if (this.skipHScroll) {
            this.skipHScroll = false;
            return;
        }

        if (this.hScrollTimer)
            clearTimeout(this.hScrollTimer);

        const value = (event.target as HTMLElement).scrollLeft;
        if (this.hScrollPosition === value && !this.wheelScroll)
            return;

        this.hScrollPosition = value;
        this.wheelScroll = false;
        this.hScrollTimer = setTimeout(() => {
            this.hScrollTimer = null;
            this.hScrollPositionCoeff = (this.hScrollPosition + this.bufferChartRef.nativeElement.offsetWidth) / this.hScrollWidth;

            const max = (this.data.rows.length + this.extraScrollDays) * this.hScrollPositionCoeff;
            const min = max - this.data.visibleDaysCount;
            this.bufferChartOptions.hAxis.viewWindow.min = min;
            this.bufferChartOptions.hAxis.viewWindow.max = max;

            this.bufferChart.draw();
        },
            100);
    }

    private onMouseWheel = (event: WheelEvent): void => {
        //switch(event.deltaMode) {
        //    case 0://DOM_DELTA_PIXEL
        //        break;
        //    case 1://DOM_DELTA_LINE
        //        break;
        //    case 2://DOM_DELTA_PAGE
        //        break;
        //}

        const maxScroll = this.hScrollWidth - this.bufferChartRef.nativeElement.offsetWidth;
        let scrollPosition = this.hScrollPosition + event.deltaY;
        if (scrollPosition > maxScroll)
            scrollPosition = maxScroll;
        else if (scrollPosition < 0)
            scrollPosition = 0;

        this.wheelScroll = true;
        this.hScrollPosition = scrollPosition;
    }

    private onChangeBufferClick = (): void => {
        this.changeBuffer.emit();
    }
}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/components/buffer-graph-chart.component/buffer-graph-chart.component.ts